import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { marked } from "marked";
import html2pdf from "html2pdf.js";
import {
  setCanvasExporting,
  setCanvasSavingToResources,
  setCanvasSavingToDocuments,
  setCanvasGeneratingTitle,
} from "../../../redux/features/agent/agentSlice";
import { selectUser } from "../../../redux/features/appData";
import { CanvasModeService } from "../../../services/CanvasModeService";
import { toastSuccess, toastFail } from "../../../config";

export const useCanvasExport = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Export document as PDF using client-side generation
  const exportPdf = useCallback(
    async (title: string, content: string, filename?: string) => {
      dispatch(setCanvasExporting(true));

      try {
        const html = marked(content) as string;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        tempDiv.style.cssText = `
          font-family: Arial, sans-serif;
          padding: 20px;
          max-width: 8.2in;
        `;

        const fileName =
          filename || `${title}-${new Date().toISOString().split("T")[0]}.pdf`;
        const options = {
          margin: 0.5,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "letter" },
        };

        document.body.appendChild(tempDiv);
        await html2pdf().set(options).from(tempDiv).save();
        document.body.removeChild(tempDiv);

        toast("PDF exported successfully", toastSuccess);
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast("Failed to export PDF", toastFail);
      } finally {
        dispatch(setCanvasExporting(false));
      }
    },
    [dispatch]
  );

  // Save document to resources (knowledge base)
  const saveToResources = useCallback(
    async (messageId: number, title: string, description?: string) => {
      if (!user?.currentWorkspaceId) {
        toast("No workspace selected", toastFail);
        return { success: false };
      }

      dispatch(setCanvasSavingToResources(true));

      try {
        toast("Saving to resources...", toastSuccess);
        const result = await CanvasModeService.saveToResources({
          messageId,
        });

        if (result?.success) {
          toast(`"${title}" saved to resources`, toastSuccess);
        } else {
          toast("Failed to save to resources", toastFail);
        }

        return result ?? { success: false };
      } catch (error) {
        console.error("Error saving to resources:", error);
        toast("Failed to save to resources", toastFail);
        return { success: false };
      } finally {
        dispatch(setCanvasSavingToResources(false));
      }
    },
    [dispatch, user]
  );

  // Save document to documents section
  const saveToDocuments = useCallback(
    async (
      messageId: number,
      title: string,
      description?: string,
      isPublic?: boolean,
      requiresSignatures?: boolean
    ) => {
      if (!user?.currentWorkspaceId) {
        toast("No workspace selected", toastFail);
        return;
      }

      dispatch(setCanvasSavingToDocuments(true));

      try {
        const success = await CanvasModeService.saveToDocuments({
          messageId,
          title,
          description,
        });

        if (success) {
          toast(`"${title}" saved to documents`, toastSuccess);
        } else {
          toast("Failed to save to documents", toastFail);
        }
      } catch (error) {
        console.error("Error saving to documents:", error);
        toast("Failed to save to documents", toastFail);
      } finally {
        dispatch(setCanvasSavingToDocuments(false));
      }
    },
    [dispatch, user]
  );

  const copyMarkdownToClipboard = useCallback(
    async (markdown: string): Promise<boolean> => {
      const html = marked(markdown) as string;
      if (navigator.clipboard && navigator.clipboard.write) {
        try {
          const blobInput = new Blob([html], { type: "text/html" });
          const clipboardItem = new ClipboardItem({
            "text/html": blobInput,
            "text/plain": new Blob([markdown], { type: "text/plain" }),
          });

          await navigator.clipboard.write([clipboardItem]);
          return true;
        } catch (err) {
          return false;
        }
      } else {
        return false;
      }
    },
    []
  );

  const copyAsRTF = useCallback(
    async (content: string) => {
      try {
        const success = await copyMarkdownToClipboard(content);

        if (success) {
          toast("Document copied to clipboard as rich text", toastSuccess);
        } else {
          toast("Failed to copy to clipboard", toastFail);
        }
      } catch (error) {
        console.error("Error copying as RTF:", error);
        toast.error("Failed to copy to clipboard");
      }
    },
    [copyMarkdownToClipboard]
  );

  // Generate AI title for document
  const generateTitle = useCallback(
    async (
      messageId: number,
      content: string,
      title: string = ""
    ): Promise<string | null> => {
      dispatch(setCanvasGeneratingTitle(true));

      try {
        const generatedTitle = await CanvasModeService.generateTitle({
          messageId,
          content,
          title,
        });

        return generatedTitle || "Untitled Document";
      } catch (error) {
        console.error("Error generating title:", error);
        return null;
      } finally {
        dispatch(setCanvasGeneratingTitle(false));
      }
    },
    [dispatch]
  );

  return {
    exportPdf,
    saveToResources,
    saveToDocuments,
    copyAsRTF,
    generateTitle,
  };
};
